import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Link,
  Menu,
  MenuItem,
  Breadcrumbs as MuiBreadcrumbs,
  Tooltip,
  alpha,
} from '@mui/material'

import { Typography } from '~components/internal'
import { borderRadius, colors, spacing } from '~tokens/internal'

import Icon from './Icon'

export interface BreadcrumbItem {
  label: string
  href?: string
  onClick?: () => void
  truncate?: boolean
  suffix?: React.ReactNode
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  maxItems?: number
  separator?: React.ReactNode
}

const LINK_COLOR = colors.secondary[700]

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  maxItems = 4,
  separator = (
    <Typography
      component='span'
      sx={{
        color: 'text.secondary',
      }}
    >
      /
    </Typography>
  ),
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleEllipsisClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (item: BreadcrumbItem): void => {
    handleClose()
    if (item.onClick) {
      item.onClick()
    }
  }

  const linkSx = { color: LINK_COLOR, cursor: 'pointer' }

  const buildGridTemplate = (count: number, collapsed = false): string => {
    if (collapsed) {
      // first item / separator / ellipsis box / separator / last item
      return 'auto auto auto auto minmax(0, 1fr)'
    }
    const columns: string[] = []
    for (let i = 0; i < count; i++) {
      if (i < count - 1) {
        columns.push('auto', 'auto') // item + separator
      } else {
        columns.push('minmax(0, 1fr)') // last item fills remaining space
      }
    }
    return columns.join(' ')
  }

  const normalGridTemplate = buildGridTemplate(items.length)
  const collapsedGridTemplate = buildGridTemplate(items.length, true)

  const renderTruncatedLabel = (item: BreadcrumbItem, index: number): React.ReactElement => {
    const tooltipTitle = `${item.label}${typeof item.suffix === 'string' ? item.suffix : ''}`
    return (
      <Tooltip key={index} title={tooltipTitle} arrow placement='top'>
        <Box
          component='span'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Typography
            component='span'
            variant='body1'
            noWrap
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.primary',
            }}
          >
            {item.label}
          </Typography>
          {item.suffix && (
            <Typography
              component='span'
              variant='body1'
              noWrap
              sx={{ flexShrink: 0, color: 'text.secondary' }}
            >
              {item.suffix}
            </Typography>
          )}
        </Box>
      </Tooltip>
    )
  }

  const renderLink = (item: BreadcrumbItem, index: number): React.ReactElement => {
    const linkProps = {
      underline: 'hover' as const,
      sx: linkSx,
      noWrap: item.truncate,
    }

    if (item.href) {
      const link = (
        <Link
          key={index}
          component={RouterLink}
          to={item.href}
          onClick={item.onClick}
          {...linkProps}
        >
          {item.label}
        </Link>
      )
      return item.truncate ? (
        <Tooltip key={index} title={item.label} arrow placement='top'>
          {link}
        </Tooltip>
      ) : (
        link
      )
    }

    const link = (
      <Link
        key={index}
        href='#'
        onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
          e.preventDefault()
          item.onClick?.()
        }}
        {...linkProps}
      >
        {item.label}
      </Link>
    )
    return item.truncate ? (
      <Tooltip key={index} title={item.label} arrow placement='top'>
        {link}
      </Tooltip>
    ) : (
      link
    )
  }

  // If items fit within maxItems, render normally
  if (items.length <= maxItems) {
    return (
      <MuiBreadcrumbs
        separator={separator}
        sx={{
          maxWidth: '100%',
          flexWrap: 'nowrap',
          '& .MuiBreadcrumbs-ol': {
            display: 'grid',
            gridTemplateColumns: normalGridTemplate,
            alignItems: 'center',
            minWidth: 0,
            width: '100%',
          },
          '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden' },
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          if (isLast) {
            if (!item.truncate && !item.suffix) {
              return (
                <Typography key={index} variant='body1' sx={{ color: 'text.primary' }}>
                  {item.label}
                </Typography>
              )
            }
            return renderTruncatedLabel(item, index)
          }
          return renderLink(item, index)
        })}
      </MuiBreadcrumbs>
    )
  }

  // Collapsed breadcrumbs with dropdown
  const firstItem = items[0]
  const lastItem = items[items.length - 1]
  const collapsedItems = items.slice(1, items.length - 1)

  return (
    <>
      <MuiBreadcrumbs
        separator={separator}
        sx={{
          maxWidth: '100%',
          flexWrap: 'nowrap',
          '& .MuiBreadcrumbs-ol': {
            display: 'grid',
            gridTemplateColumns: collapsedGridTemplate,
            alignItems: 'center',
            minWidth: 0,
            width: '100%',
          },
          '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden' },
        }}
      >
        {renderLink(firstItem, 0)}
        <Box
          component='span'
          onClick={handleEllipsisClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: LINK_COLOR,
            borderRadius: borderRadius.full,
            width: spacing.sm,
            height: spacing.sm,
            '&:hover': {
              backgroundColor: alpha(LINK_COLOR, colors.opacity.hover),
            },
          }}
        >
          <Icon name='moreHoriz' fontSize='small' />
        </Box>
        {lastItem.truncate || lastItem.suffix ? (
          renderTruncatedLabel(lastItem, items.length - 1)
        ) : (
          <Typography
            variant='body1'
            sx={{
              color: 'text.primary',
            }}
          >
            {lastItem.label}
          </Typography>
        )}
      </MuiBreadcrumbs>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: { sx: { mt: spacing.micro } },
        }}
      >
        {collapsedItems.map((item, index) => (
          <MenuItem key={index} onClick={() => handleMenuItemClick(item)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default Breadcrumbs
